import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useModal } from "hooks/useModal";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { useWindowSize } from "hooks/useWindowSize";
import Modal from "components/DesignSystem/Modal/Modal";

import { RowSelectionState } from "react-table-8.10.7";
import InvoiceDeleteModal from "./InvoiceDeleteModal";
import InvoiceMarkAsActiveModal from "./InvoiceMarkAsActiveModal";
import InvoiceUnlinkTransactionsModal from "./InvoiceUnlinkTransactionsModal";
import InvoiceUnlinkCreditNotesModal from "./InvoiceUnlinkCreditNotesModal";
import { useInvoiceBulkAction } from "hooks/useInvoiceBulkAction";
import { useMemo } from "react";

type TableSelectionControlsProps = {
  groupId: string;
  entityId: string;
  selectedInvoices: string[];
  onSelectionChange?: (invoiceIds?: string[]) => void;
  rowSelection?: RowSelectionState;
  setRowSelection?: (
    updaterOrValue:
      | RowSelectionState
      | ((prev: RowSelectionState) => RowSelectionState)
  ) => void;
};

export type SelectedRow = {
  uuid: string;
  is_recurring_enabled: boolean;
  linked_payroll_id: string;
};

export default function TableSelectionControls({
  groupId,
  entityId,
  onSelectionChange,
  selectedInvoices,
  rowSelection,
  setRowSelection,
}: TableSelectionControlsProps) {
  const { isMobile } = useWindowSize();
  const markAsActiveModal = useModal();
  const deleteModal = useModal();
  const unlinkTransactionsModal = useModal();
  const unlinkCreditNotesModal = useModal();

  const newSelectedRows = useMemo(
    () =>
      Object.entries(rowSelection || {})
        .filter(([_, isSelected]) => isSelected)
        .map(([uuid]) => uuid),
    [rowSelection]
  );

  const { shouldShowWarningModal } = useInvoiceBulkAction({
    groupId,
    entityId,
    selectedRows: selectedInvoices,
    newSelectedRows: newSelectedRows,
  });

  if (!groupId || !entityId) return null;

  const deleteModalSize = shouldShowWarningModal ? "small" : "xl";

  return (
    <>
      {selectedInvoices && selectedInvoices.length > 0 && (
        <>
          {isMobile && (
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.REVENUE}
              blockedUi={<LockedButton text="Actions" />}
            >
              <Dropdown.Root>
                <Dropdown.Trigger asChild className="t-group">
                  <div>
                    <Button size="small">
                      <span className="t-flex t-items-center t-gap-1">
                        Actions
                        <span className="group-data-state-open:-t-rotate-180 t-transition-transform">
                          <ArrowDown color="currentColor" size="16" />
                        </span>
                      </span>
                    </Button>
                  </div>
                </Dropdown.Trigger>
                <Dropdown.Portal>
                  <Dropdown.Content side="top" align="end" sideOffset={8}>
                    <Dropdown.Item onSelect={markAsActiveModal.open}>
                      Mark as Active
                    </Dropdown.Item>
                    <Dropdown.Item onSelect={deleteModal.open}>
                      Delete
                    </Dropdown.Item>
                    <Dropdown.Item onSelect={unlinkTransactionsModal.open}>
                      Unlink Transactions
                    </Dropdown.Item>
                    <Dropdown.Item onSelect={unlinkCreditNotesModal.open}>
                      Unlink Credit Notes
                    </Dropdown.Item>
                  </Dropdown.Content>
                </Dropdown.Portal>
              </Dropdown.Root>
            </LockedUi>
          )}
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.REVENUE}
            blockedUi={isMobile ? null : <LockedButton text="Mark as Active" />}
          >
            <Modal.Root
              open={markAsActiveModal.isOpen}
              onOpenChange={markAsActiveModal.toggle}
            >
              {!isMobile && (
                <Modal.Trigger asChild>
                  <Button size="small">Mark as Active</Button>
                </Modal.Trigger>
              )}
              <Modal.Content size="xl">
                <InvoiceMarkAsActiveModal
                  groupId={groupId}
                  entityId={entityId}
                  selectedRows={selectedInvoices}
                  onSuccess={() => {
                    markAsActiveModal.close();
                    onSelectionChange?.([]);
                  }}
                  rowSelection={rowSelection}
                />
              </Modal.Content>
            </Modal.Root>
          </LockedUi>

          <LockedUi
            feature={WRITE_PERMISSION_KEYS.REVENUE}
            blockedUi={isMobile ? null : <LockedButton text="Delete" />}
          >
            <Modal.Root
              open={deleteModal.isOpen}
              onOpenChange={deleteModal.toggle}
            >
              {!isMobile && (
                <Modal.Trigger asChild>
                  <Button size="small">Delete</Button>
                </Modal.Trigger>
              )}
              <Modal.Content size={deleteModalSize}>
                <InvoiceDeleteModal
                  groupId={groupId}
                  entityId={entityId}
                  selectedRows={selectedInvoices}
                  rowSelection={rowSelection}
                  success={() => {
                    deleteModal.close();
                    onSelectionChange?.([]);
                  }}
                />
              </Modal.Content>
            </Modal.Root>
          </LockedUi>

          <LockedUi
            feature={WRITE_PERMISSION_KEYS.REVENUE}
            blockedUi={
              isMobile ? null : <LockedButton text="Unlink Transactions" />
            }
          >
            <Modal.Root
              open={unlinkTransactionsModal.isOpen}
              onOpenChange={unlinkTransactionsModal.toggle}
            >
              {!isMobile && (
                <Modal.Trigger asChild>
                  <Button size="small">Unlink Transactions</Button>
                </Modal.Trigger>
              )}
              <Modal.Content size="small">
                <InvoiceUnlinkTransactionsModal
                  groupId={groupId}
                  entityId={entityId}
                  selectedRows={selectedInvoices}
                  success={() => {
                    unlinkTransactionsModal.close();
                    onSelectionChange?.([]);
                  }}
                />
              </Modal.Content>
            </Modal.Root>
          </LockedUi>

          <LockedUi
            feature={WRITE_PERMISSION_KEYS.REVENUE}
            blockedUi={
              isMobile ? null : <LockedButton text="Unlink Credit Notes" />
            }
          >
            <Modal.Root
              open={unlinkCreditNotesModal.isOpen}
              onOpenChange={unlinkCreditNotesModal.toggle}
            >
              {!isMobile && (
                <Modal.Trigger asChild>
                  <Button size="small">Unlink Credit Notes</Button>
                </Modal.Trigger>
              )}
              <Modal.Content size="small">
                <InvoiceUnlinkCreditNotesModal
                  groupId={groupId}
                  entityId={entityId}
                  selectedRows={selectedInvoices}
                  success={() => {
                    unlinkCreditNotesModal.close();
                    onSelectionChange?.([]);
                  }}
                />
              </Modal.Content>
            </Modal.Root>
          </LockedUi>
        </>
      )}
    </>
  );
}
